import { createActionHelpers } from 'vuex-loading';
import _ from 'lodash';

const { startLoading, endLoading } = createActionHelpers({
    moduleName: 'loading',
});

const state = {
    customerEngagementRules: [
        {
            id: null,
            title: null,
            type: null,
            trigger_priority: 1,
            trigger_type: null,
            enabled: false,
        },
    ],

    gameTypeList: [],

    gameList: [],

    campaignOrderList: [],

    filterOutlets: [],

    filterTypes: [],

    upsellConfig: [],
};

const getters = {
    customerEngagementRulesImmutable: state =>
        Object.values(state.customerEngagementRules),

    customerEngagementRules: state => {
        const rules = state.customerEngagementRules;
        return _.sortBy(rules, rule => {
            return rule.trigger_priority;
        });
    },

    gameTypeList: state => state.gameTypeList,

    gameList: state => state.gameList,

    customerEngagementGamification: state => {
        return state.customerEngagementRules.find(rule => rule.type === 'game');
    },

    campaignOrderList: state => {
        const list = state.campaignOrderList;
        return _.sortBy(list, item => {
            return item.order_priority;
        });
    },

    upsellConfigImmutable: state => Object.values(state.upsellConfig),

    upsellConfig: state => {
        const configs = state.upsellConfig;
        return _.sortBy(configs, config => {
            return config.order_priority;
        });
    },
};

const mutations = {
    setCustomerEngagementRules(state, data) {
        state.customerEngagementRules = data;
    },

    setCustomerEngagementRuleAttribute(state, data) {
        const ruleIndex = state.customerEngagementRules.findIndex(
            rule => rule.id === data.ruleId,
        );

        if (ruleIndex === -1) {
            return;
        }

        state.customerEngagementRules[ruleIndex][data.key] = data.value;
    },

    setGameTypeList(state, data) {
        state.gameTypeList = data;
    },

    setGameList(state, data) {
        state.gameList = data;
    },

    setCustomerEngagementEngagementTriggerType(state, { rule, triggerType }) {
        rule.trigger_type = triggerType;
        state.customerEngagementGamification = rule;
    },

    setFilterOutlets(state, data) {
        state.filterOutlets = data;
    },

    setFilterTypes(state, data) {
        state.filterTypes = data;
    },

    setCampaignOrderList(state, data) {
        state.campaignOrderList = data;
    },

    setCampaignOrderListAttribute(state, data) {
        const listIndex = state.campaignOrderList.findIndex(
            list => list.id === data.listId,
        );

        if (listIndex === -1) {
            return;
        }

        state.campaignOrderList[listIndex][data.key] = data.value;

        if (data.oldOrderPriority) {
            state.campaignOrderList[listIndex]['old_order_priority'] =
                data.oldOrderPriority;
        }
    },

    setUpsellConfig(state, data) {
        state.upsellConfig = data;
    },

    setUpsellConfigAttribute(state, data) {
        const configIndex = state.upsellConfig.findIndex(
            config => config.id === data.configId,
        );

        if (configIndex === -1) {
            return;
        }

        state.upsellConfig[configIndex][data.key] = data.value;

        if (data.oldOrderPriority) {
            state.upsellConfig[configIndex]['old_order_priority'] =
                data.oldOrderPriority;
        }
    },
};

const actions = {
    getCustomerEngagementRules({ commit, dispatch }) {
        startLoading(dispatch, 'get-customer-engagement-rules');

        return axios
            .get(Laroute.route('franchise.customer.engagement.rule.list'))
            .then(res => {
                commit('setCustomerEngagementRules', res.data);
                endLoading(dispatch, 'get-customer-engagement-rules');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'get-customer-engagement-rules');
                throw err;
            });
    },

    saveCustomerEngagementRules({ commit, dispatch, state }) {
        startLoading(dispatch, 'save-customer-engagement-rules');

        return axios
            .post(
                Laroute.route('franchise.customer.engagement.rule.list.save'),
                state.customerEngagementRules,
            )
            .then(res => {
                commit('setCustomerEngagementRules', res.data);
                endLoading(dispatch, 'save-customer-engagement-rules');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'save-customer-engagement-rules');
                throw err;
            });
    },

    setGameTypeList({ commit, dispatch }) {
        startLoading(dispatch, 'get-customer-engagement-game-types');

        return axios
            .get(Laroute.route('franchise.customer.engagement.game.type'))
            .then(res => {
                commit('setGameTypeList', res.data);
                endLoading(dispatch, 'get-customer-engagement-game-types');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'get-customer-engagement-game-types');
                throw err;
            });
    },

    getGameList({ commit, dispatch }) {
        startLoading(dispatch, 'get-game-list');

        return axios
            .get(Laroute.route('franchise.customer.engagement.game.list'))
            .then(res => {
                commit('setGameList', res.data.data);
                endLoading(dispatch, 'get-game-list');
                return res.data.data;
            })
            .catch(err => {
                endLoading(dispatch, 'get-game-list');
                throw err;
            });
    },

    getCampaignOrderList({ commit, dispatch }) {
        startLoading(dispatch, 'get-campaign-order-list');

        return axios
            .get(
                Laroute.route(
                    'franchise.customer.engagement.campaign.order.list',
                ),
            )
            .then(res => {
                commit('setCampaignOrderList', res.data);
                endLoading(dispatch, 'get-campaign-order-list');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'get-campaign-order-list');
                throw err;
            });
    },

    saveCustomerEngagementCampaign({ commit, dispatch, getters }) {
        startLoading(dispatch, 'save-customer-engagement-campaign');

        const data = {
            customer_engagement: getters.customerEngagementGamification,
            campaign_order_list: getters.campaignOrderList,
        };

        return axios
            .post(
                Laroute.route('franchise.customer.engagement.campaign.save'),
                data,
            )
            .then(res => {
                endLoading(dispatch, 'save-customer-engagement-campaign');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'save-customer-engagement-campaign');
                throw err;
            });
    },

    getActiveCampaignCount({ dispatch }) {
        return axios
            .get(
                Laroute.route(
                    'franchise.customer.engagement.campaign.active.count',
                ),
            )
            .then(res => {
                return res.data.count;
            });
    },

    createCampaign({ dispatch }, campaignData) {
        startLoading(dispatch, 'create-campaign');

        return axios
            .post(
                Laroute.route('franchise.customer.engagement.campaign.create'),
                campaignData,
            )
            .then(res => {
                endLoading(dispatch, 'create-campaign');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'create-campaign');
                throw err;
            });
    },

    getCampaign({ dispatch }, campaignId) {
        startLoading(dispatch, 'get-campaign');

        return axios
            .get(
                Laroute.route('franchise.customer.engagement.campaign.show', {
                    id: campaignId,
                }),
            )
            .then(res => {
                endLoading(dispatch, 'get-campaign');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'get-campaign');
                throw err;
            });
    },

    updateCampaign({ dispatch }, { id, data }) {
        startLoading(dispatch, 'update-campaign');

        return axios
            .post(
                Laroute.route('franchise.customer.engagement.campaign.update', {
                    id: id,
                }),
                data,
            )
            .then(res => {
                endLoading(dispatch, 'update-campaign');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'update-campaign');
                throw err;
            });
    },

    getGame({ dispatch }, gameId) {
        startLoading(dispatch, 'get-game');

        return axios
            .get(
                Laroute.route('franchise.customer.engagement.game.show', {
                    id: gameId,
                }),
            )
            .then(res => {
                endLoading(dispatch, 'get-game');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'get-game');
                throw err;
            });
    },

    createGame({ dispatch }, formData) {
        startLoading(dispatch, 'create-game');

        return axios
            .post(
                Laroute.route('franchise.customer.engagement.game.create'),
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                },
            )
            .then(res => {
                endLoading(dispatch, 'create-game');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'create-game');
                throw err;
            });
    },

    updateGame({ dispatch }, { id, data }) {
        startLoading(dispatch, 'update-game');

        return axios
            .post(
                Laroute.route('franchise.customer.engagement.game.update', {
                    id: id,
                }),
                data,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                },
            )
            .then(res => {
                endLoading(dispatch, 'update-game');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'update-game');
                throw err;
            });
    },

    getUpsellConfig({ commit, dispatch }) {
        startLoading(dispatch, 'get-upsell-config');
        return axios
            .get(Laroute.route('franchise.customer.engagement.upsell.config'))
            .then(res => {
                commit('setUpsellConfig', res.data);
                endLoading(dispatch, 'get-upsell-config');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'get-upsell-config');
                throw err;
            });
    },

    saveUpsellConfig({ commit, dispatch, getters }) {
        startLoading(dispatch, 'save-upsell-config');

        return axios
            .post(
                Laroute.route(
                    'franchise.customer.engagement.upsell.config.save',
                ),
                getters.upsellConfig,
            )
            .then(res => {
                commit('setUpsellConfig', res.data);
                endLoading(dispatch, 'save-upsell-config');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'save-upsell-config');
                throw err;
            });
    },

    // Loading state for get is managed by the form component, not here.
    getCategoryUpsell(_, id) {
        return axios
            .get(
                Laroute.route(
                    'franchise.customer.engagement.upsell.category.show',
                    { id },
                ),
            )
            .then(res => res.data);
    },

    createCategoryUpsell({ dispatch }, data) {
        startLoading(dispatch, 'save-category-upsell');

        return axios
            .post(
                Laroute.route(
                    'franchise.customer.engagement.upsell.category.create',
                ),
                data,
            )
            .then(res => {
                endLoading(dispatch, 'save-category-upsell');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'save-category-upsell');
                throw err;
            });
    },

    updateCategoryUpsell({ dispatch }, { id, data }) {
        startLoading(dispatch, 'save-category-upsell');

        return axios
            .post(
                Laroute.route(
                    'franchise.customer.engagement.upsell.category.update',
                    { id },
                ),
                data,
            )
            .then(res => {
                endLoading(dispatch, 'save-category-upsell');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'save-category-upsell');
                throw err;
            });
    },

    deleteCategoryUpsells({ dispatch }, ids) {
        startLoading(dispatch, 'delete-category-upsell');

        return axios
            .delete(
                Laroute.route(
                    'franchise.customer.engagement.upsell.category.delete',
                ),
                { data: { ids } },
            )
            .then(res => {
                endLoading(dispatch, 'delete-category-upsell');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'delete-category-upsell');
                throw err;
            });
    },

    // Loading state for get is managed by the form component, not here.
    getTimeUpsell(_, id) {
        return axios
            .get(
                Laroute.route(
                    'franchise.customer.engagement.upsell.time.show',
                    { id },
                ),
            )
            .then(res => res.data);
    },

    createTimeUpsell({ dispatch }, data) {
        startLoading(dispatch, 'save-time-upsell');

        return axios
            .post(
                Laroute.route(
                    'franchise.customer.engagement.upsell.time.create',
                ),
                data,
            )
            .then(res => {
                endLoading(dispatch, 'save-time-upsell');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'save-time-upsell');
                throw err;
            });
    },

    updateTimeUpsell({ dispatch }, { id, data }) {
        startLoading(dispatch, 'save-time-upsell');

        return axios
            .post(
                Laroute.route(
                    'franchise.customer.engagement.upsell.time.update',
                    { id },
                ),
                data,
            )
            .then(res => {
                endLoading(dispatch, 'save-time-upsell');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'save-time-upsell');
                throw err;
            });
    },

    deleteTimeUpsells({ dispatch }, ids) {
        startLoading(dispatch, 'delete-time-upsell');

        return axios
            .delete(
                Laroute.route(
                    'franchise.customer.engagement.upsell.time.delete',
                ),
                { data: { ids } },
            )
            .then(res => {
                endLoading(dispatch, 'delete-time-upsell');
                return res.data;
            })
            .catch(err => {
                endLoading(dispatch, 'delete-time-upsell');
                throw err;
            });
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
